import { LANE_HEIGHT, ROW_PADDING_Y, type BoardSpan } from './geometry';

/**
 * Overlap → lanes. Pure, no React.
 */

export interface LaneInput {
  key: string;
  span: BoardSpan;
}

/**
 * Greedy interval packing: each entry drops into the first lane it does not collide with, so
 * nothing is ever hidden behind anything else — the row simply grows taller.
 *
 * Keyed by **string**, deliberately. `worker-calendar`'s `overlapLayout.ts` keys a flat
 * `Map<number, …>` by owner id, so an appointment and a task sharing an id overwrite each other.
 */
export function packIntoLanes(items: LaneInput[]): {
  lanes: Map<string, number>;
  laneCount: number;
} {
  const sorted = [...items].sort(
    (a, b) => a.span.startMin - b.span.startMin || a.span.endMin - b.span.endMin,
  );

  /** `laneEnds[i]` = where the last entry placed in lane `i` finishes. */
  const laneEnds: number[] = [];
  const lanes = new Map<string, number>();

  for (const item of sorted) {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > item.span.startMin) {
      lane += 1;
    }
    if (lane === laneEnds.length) laneEnds.push(0);
    laneEnds[lane] = item.span.endMin;
    lanes.set(item.key, lane);
  }

  return { lanes, laneCount: laneEnds.length };
}

/** Total height of a row given how many lanes it needs. */
export function rowHeightPx(laneCount: number): number {
  return Math.max(1, laneCount) * LANE_HEIGHT + ROW_PADDING_Y * 2;
}
